import type { StatementImportOperationV2 } from "@tradvera/contracts";

const PREFIX = "tradvera.portfolio-v2.statement-operation-pending.v2";
const WRITES = ["UPLOAD", "REVIEW", "RECONCILIATION", "ACCEPT_INTENT", "ACCEPT_CONFIRM", "DISCARD"] as const;

export type DurableStatementWriteOperationV2 = Exclude<StatementImportOperationV2, "EXTRACTION">;

export interface PendingStatementOperationV2 {
  operation: DurableStatementWriteOperationV2;
  bookId: string;
  importId: string | null;
  targetReviewRevision: number | null;
  idempotencyKey: string;
  inputFingerprint: string;
  /** Local safety timestamp only. Older deployed markers do not contain it. */
  createdAtEpochMs?: number;
}

export type PendingStatementOperationReadV2 = { state: "ABSENT" }
  | { state: "INVALID" | "UNAVAILABLE" }
  | { state: "VALID"; marker: PendingStatementOperationV2 };

export type PendingStatementOperationFinalizeV2 = { state: "ABSENT" }
  | { state: "REPLACED"; marker: PendingStatementOperationV2 }
  | { state: "INVALID" | "UNAVAILABLE" };

export function pendingStatementOperationStorageKeyV2(ownerId: string, bookId: string): string {
  return `${PREFIX}:${encodeURIComponent(requiredScope(ownerId))}:${encodeURIComponent(requiredScope(bookId))}`;
}

export async function withPendingStatementOperationLockV2<T>(ownerId: string, bookId: string,
  work: () => Promise<T>): Promise<T> {
  const name = `tradvera.statement-operation:${encodeURIComponent(requiredScope(ownerId))}:${encodeURIComponent(
    requiredScope(bookId))}`;
  const locks = typeof navigator === "undefined" ? undefined : navigator.locks;
  return locks ? locks.request(name, { mode: "exclusive" }, work) : work();
}

export function readPendingStatementOperationV2(storage: Pick<Storage, "getItem">,
  ownerId: string, bookId: string): PendingStatementOperationReadV2 {
  try {
    const raw = storage.getItem(pendingStatementOperationStorageKeyV2(ownerId, bookId));
    if (raw === null) return { state: "ABSENT" };
    const value = JSON.parse(raw) as unknown;
    return isMarker(value, ownerId, bookId) ? { state: "VALID", marker: value } : { state: "INVALID" };
  } catch { return { state: "UNAVAILABLE" }; }
}

export function persistPendingStatementOperationV2(storage: Pick<Storage, "getItem" | "setItem">,
  ownerId: string, marker: PendingStatementOperationV2): void {
  const before = readPendingStatementOperationV2(storage, ownerId, marker.bookId);
  if (before.state !== "ABSENT") throw new Error(`STATEMENT_IMPORT_PENDING_OPERATION_${before.state}`);
  const immediatelyBeforeWrite = readPendingStatementOperationV2(storage, ownerId, marker.bookId);
  if (immediatelyBeforeWrite.state !== "ABSENT") {
    throw new Error(`STATEMENT_IMPORT_PENDING_OPERATION_${immediatelyBeforeWrite.state}`);
  }
  storage.setItem(pendingStatementOperationStorageKeyV2(ownerId, marker.bookId), JSON.stringify(marker));
  const verified = readPendingStatementOperationV2(storage, ownerId, marker.bookId);
  if (verified.state !== "VALID" || !samePendingStatementOperationV2(verified.marker, marker)) {
    throw new Error("STATEMENT_IMPORT_PENDING_OPERATION_WRITE_UNVERIFIED");
  }
}

export function finalizePendingStatementOperationV2(storage: Pick<Storage, "getItem" | "removeItem">,
  ownerId: string, marker: PendingStatementOperationV2): PendingStatementOperationFinalizeV2 {
  const current = readPendingStatementOperationV2(storage, ownerId, marker.bookId);
  if (current.state === "VALID" && samePendingStatementOperationV2(current.marker, marker)) {
    storage.removeItem(pendingStatementOperationStorageKeyV2(ownerId, marker.bookId));
  } else if (current.state === "VALID") {
    return { state: "REPLACED", marker: current.marker };
  } else if (current.state !== "ABSENT") {
    return current;
  }
  const after = readPendingStatementOperationV2(storage, ownerId, marker.bookId);
  if (after.state === "ABSENT") return after;
  if (after.state === "VALID") return { state: "REPLACED", marker: after.marker };
  return after;
}

export function samePendingStatementOperationV2(
  left: PendingStatementOperationV2, right: PendingStatementOperationV2,
): boolean {
  return left.operation === right.operation && left.bookId === right.bookId
    && left.importId === right.importId && left.targetReviewRevision === right.targetReviewRevision
    && left.idempotencyKey === right.idempotencyKey
    && left.inputFingerprint === right.inputFingerprint;
}

function isMarker(value: unknown, ownerId: string, bookId: string): value is PendingStatementOperationV2 {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const source = value as Record<string, unknown>;
  const requiredKeys = ["operation", "bookId", "importId", "targetReviewRevision",
    "idempotencyKey", "inputFingerprint"];
  const allowedKeys = [...requiredKeys, "createdAtEpochMs"];
  const sourceKeys = Object.keys(source);
  const operation = source.operation;
  return requiredKeys.every((key) => Object.hasOwn(source, key))
    && sourceKeys.length >= requiredKeys.length && sourceKeys.length <= allowedKeys.length
    && sourceKeys.every((key) => allowedKeys.includes(key))
    && typeof operation === "string" && (WRITES as readonly string[]).includes(operation)
    && source.bookId === bookId && validId(source.bookId) && validId(ownerId)
    && (operation === "UPLOAD" ? source.importId === null : validId(source.importId))
    && (operation === "REVIEW" ? Number.isSafeInteger(source.targetReviewRevision)
      && Number(source.targetReviewRevision) > 0 : source.targetReviewRevision === null)
    && validId(source.idempotencyKey)
    && (source.createdAtEpochMs === undefined || (Number.isSafeInteger(source.createdAtEpochMs)
      && Number(source.createdAtEpochMs) > 0))
    && typeof source.inputFingerprint === "string" && /^sha256:v1:[a-f0-9]{64}$/u.test(source.inputFingerprint);
}

function validId(value: unknown): value is string {
  return typeof value === "string" && value.length >= 3 && value.length <= 160
    && /^[A-Za-z0-9._:/-]+$/u.test(value);
}

function requiredScope(value: string): string {
  const result = value.trim();
  if (!validId(result)) throw new Error("STATEMENT_IMPORT_STORAGE_SCOPE_INVALID");
  return result;
}
